import {_} from 'lodash';
import * as ko from "knockout";
import PromiseRequest from "../../../req/PromiseRequest";
import pubsub from "../../../pubsub";
import {AVAILABLE_CSV_DELIMITERS, MAX_CONCURRENT_REQUESTS} from "../../../constants";
import {ButtonManager} from "../../../util/ButtonManager";
import {IMapping} from "./index";
import {IMessageData, IObjectData, IRouteDefinitionParams, ISyncField, ITableColumn} from "../../../index";
import {IRequestObject} from "../../../req/index";
import {ISortableTableParams} from "../../sortable/index";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {PLEASE_WAIT_MESSAGE, REQUEST_MESSAGE, UPLOADING_MESSAGE} from "../../../constants/Language";
import {createErrorMessage, getObjectType, getSourceTranslation, isPartOf} from "../../../util/misc";
import {getBackButton, getCancelButton, getContinueButton, getDiscardButton, getHash} from "../../../constants/RouteDefinitions";
import {global, lm} from "../../../appContext";
import {nsFa} from "../../fa/fn";
import {nsLanguage} from "../../../util/nsLanguage";
import {nsObjectUpload} from "./fn";
import {nsRoute} from "../../../util/route";
import {nsSyncFields} from "../../../req/nsSyncFields";
import {storeObservable} from "../../../util/sessionstore";
import {IComboboxV2Options} from "../../combobox/v2";
import {nsTooltip} from "../../../util/nsTooltip";
import {nsCsv} from "../../../util/nsCsv";
import AssignMode = nsObjectUpload.AssignMode;
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import Step = nsObjectUpload.Step;
import getNumObjectsLabel = nsLanguage.getNumObjectsLabel;
import i = nsFa.i;
import AbstractUploadHtml from './AbstractUpload.html?raw';

export let template = AbstractUploadHtml;

export abstract class AbstractUpload extends ParsedRouteDataDecorator {
    private AssignMode = AssignMode;
    private parsedCsv!: nsCsv.IParsedCsv;
    private readonly AVAILABLE_CSV_DELIMITERS = AVAILABLE_CSV_DELIMITERS;
    private readonly Step = Step;
    private readonly createError: KnockoutObservableArray<{ row: string[], reason: string }> = ko.observableArray();
    private readonly createSuccess: KnockoutObservableArray<IObjectData> = ko.observableArray();
    private readonly delimiter: KnockoutObservable<nsCsv.TDelimiter> = storeObservable('csv.delimiter');
    private readonly et = global.errorAreas.get('upload.manual.object.from.csv');
    private readonly file: KnockoutObservable<File | null> = ko.observable(null);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly mappings: KnockoutObservableArray<IMapping> = ko.observableArray();
    private readonly mappingsMessageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly percentComplete: KnockoutObservable<number | null> = ko.observable(null);
    private readonly requests: PromiseRequest[] = [];
    private readonly result: KnockoutObservable<string | undefined> = ko.observable();
    private readonly tableParams: KnockoutObservable<ISortableTableParams | undefined> = ko.observable();
    private reqGetSyncFields: IRequestObject | null = null;
    private syncfields: KnockoutObservable<ISyncField[] | null> = ko.observable(null)
    protected readonly step: KnockoutObservable<Step | null> = ko.observable(null);
    private pageButtons: ButtonManager = new ButtonManager()

    constructor(params: IRouteDefinitionParams) {
        super(params);

        this.kosub.subscribe(this.step, this.onStepChange.bind(this));
        this.kosub.subscribe(this.delimiter, this.parseCsvFile.bind(this));

        this.pageButtons.add(getBackButton(undefined, {
            label: 'exit',
            validator: () => this.isStep(Step.Error, Step.Success)
        }))
        this.pageButtons.add(getCancelButton(undefined, {
            validator: () => !this.isStep(Step.Error, Step.Success)
        }))
        this.pageButtons.add(getContinueButton({
            onClick: this.onNextClick.bind(this),
            enabled: this.isValid.bind(this),
            validator: () => !this.isStep(Step.Init, Step.Error, Step.Success),
        }))
        this.pageButtons.add(getDiscardButton({
            label: 'start over',
            onClick: this.onRestartClick.bind(this),
            enabled: () => !!this.file(),
            validator: () => this.isStep(Step.FormUpload, Step.Error, Step.Success),
        }))

        this.kosub.subscribe(storeObservable('language'), () => {
            this.reqGetSyncFields?.promise.abort()
            this.reqGetSyncFields = null
            this.initSyncfields()
        })
    }

    private initSyncfields(): Promise<any> {
        this.reqGetSyncFields = this.reqGetSyncFields ?? nsSyncFields.getSyncFields(this.__objectTypeId!)
        return this.reqGetSyncFields
            .request
            .then((data: ISyncField[]) => {
                this.syncfields(data);
            })
    }

    private onStepChange() {
        switch (this.step()) {
            case Step.Init: {
                this.messageData({message: REQUEST_MESSAGE});
                this.createSuccess([]);
                this.createError([]);
                this.file(null);
                this.initSyncfields()
                    .then(() => {
                        this.messageData(null);
                        this.doNextStep();
                    })
                    .catch((reason: string) => this.messageData(createErrorMessage(String(reason))));
                break;
            }

            case Step.ProcessUpload: {
                this.messageData({message: PLEASE_WAIT_MESSAGE});
                const reader = new FileReader();
                const file = this.file();
                if (!file) {
                    return;
                }
                reader.readAsText(file, 'UTF-8');
                reader.onload = (e: ProgressEvent) => {
                    this.result((e.target as FileReader).result as string);
                    this.parseCsvFile();
                    this.messageData(null);
                    this.doNextStep();
                };
                break;
            }

            case Step.ProcessTransform: {

                const orgnr = global.login.selectedOrganizationId();
                const csvRows: nsCsv.IParsedCsv['rows'] = _.clone(this.parsedCsv.rows);
                const numTotal = csvRows.length;

                const numSuccess = ko.pureComputed(() => this.createSuccess().length);
                const numError = ko.pureComputed(() => this.createError().length);

                const updatePercent = () => {
                    this.percentComplete((numSuccess() + numError()) / numTotal);
                    const percentComplete = this.percentComplete();
                    const percent = percentComplete != null ? (percentComplete * 100).toFixed(1) : '0';

                    const message = [`${UPLOADING_MESSAGE} ${percent}%`];
                    if (numSuccess()) {
                        message.push(`${i('fa-check')} ((success)): ${numSuccess()}`);
                    }
                    if (numError()) {
                        message.push(`<span class="text-danger">${i('fa-times')} ((error)): ${numError()}</span>`);
                    }
                    this.messageData({message: message.join('<br>')});
                }

                new Promise((resolve: (value: unknown) => void) => {

                    const resolveNext = () => {
                        updatePercent();
                        if (csvRows.length) {
                            doNext();
                        } else if (numSuccess() + numError() == numTotal) {
                            resolve(null);
                        }
                    }

                    const doNext = () => {
                        const csvRow: string[] | undefined = csvRows.shift();
                        if (!csvRow?.length) {
                            resolveNext();
                            return;
                        }
                        const p: PromiseRequest = new PromiseRequest('createobject');
                        const syncfields: ISyncField[] = this.mappings()
                            .map((m: IMapping) => {
                                const sf = _.clone(m.syncfield());
                                switch (m.mode()) {
                                    case nsObjectUpload.AssignMode.Dynamic: {
                                        const index = this.parsedCsv.headings.findIndex(h => h == m.heading());
                                        let val: any = csvRow[index];
                                        sf.value = val;
                                        return sf;
                                    }
                                    case nsObjectUpload.AssignMode.Static: {
                                        sf.value = m.input();
                                        return sf;
                                    }
                                }
                            })
                        ;

                        const jobject = {
                            companyid: orgnr,
                            id: 0,
                            manual: true,
                            orgnr: orgnr,
                            syncfields: syncfields,
                            type: getObjectType(this.__objectTypeId!),
                        }
                        this.requests.push(p);
                        return p
                            .request({
                                jobject: JSON.stringify(jobject),
                            })
                            .then((data: IObjectData[]) => {
                                const object = data[0];
                                if (object) {
                                    this.createSuccess.push(object);
                                }
                                resolveNext();
                            })
                            .catch((reason: string) => {
                                this.createError.push({row: csvRow, reason: reason});
                                resolveNext();
                            });
                    };

                    for (let i = 0; i < Math.min(csvRows.length, MAX_CONCURRENT_REQUESTS); i++) {
                        doNext();
                    }
                }).then(() => {
                    global.cacheManager.removeCache('getobject');
                    global.cacheManager.removeCache('searchobject');
                    pubsub.trigger("search.object.needs.refresh");
                    this.doNextStep();
                });
                break;
            }
            case Step.Success:
            case Step.Error: {
                const message: string[] = []
                message.push('((upload completed))')
                message.push(`${i('fa-check')} ((success)): ${this.createSuccess()?.length}/${this.parsedCsv.rows.length}`);
                message.push(`${i('fa-times')} ((error)): ${this.createError()?.length}/${this.parsedCsv.rows.length}`);
                this.messageData({message: message.join('<br>')})
            }
        }
    }

    private parseCsvFile() {
        this.parsedCsv = this.parseCsv(this.result());
        const syncfields = this.syncfields();
        if (!syncfields) {
            return;
        }
        const mappings = this.parsedCsv.headings.map((name: string): IMapping => {
            const nameLowerCase = name.toLowerCase();
            const syncfield = syncfields.find(s => s.name?.toLowerCase() == nameLowerCase || s.displayname?.toLowerCase() == nameLowerCase);
            return {
                heading: ko.observable(name),
                syncfield: ko.observable(syncfield),
                mode: ko.observable<AssignMode>(AssignMode.Dynamic),
                input: ko.observable(null),
            }
        });
        this.mappings(mappings);
        const tableColumns: ITableColumn[] = this.parsedCsv.headings.map(key => ({
            key: key,
            sort: true,
        }));

        const isRuleset = (rowHeading: string): boolean => {
            const mapping = this.mappings().find(m => m.heading() == rowHeading);
            const syncfield = mapping?.syncfield();
            return ((syncfield?.name.indexOf('RuleSet') ?? -1) != -1);
        }

        this.tableParams({
            TABLE: tableColumns,
            asc: ko.observable(true),
            data: this.parsedCsv.rows.map((r: string[]) => {
                const ret: any = {};
                r.forEach((val: string, index: number) => {
                    const heading = this.parsedCsv.headings[index];
                    if (heading !== undefined) {
                        ret[heading] = val;
                    }
                });
                return ret;
            }),
            getCellContent: (key: string | number | symbol, val: any) => {
                const rowHeading = String(key);
                if (isRuleset(rowHeading)) {
                    return `${i('fa-ruleset')} </fa-icon><a href="${getHash('rulesetdetails', {id: val})}">${val}</a>`;
                }
                return val;
            },
            getCellText: (key: string | number | symbol, val: any) => {
                const rowHeading = String(key);
                if (isRuleset(rowHeading)) {
                    return `ruleset`
                }
                return val;
            },
            translate: (key: string | number | symbol) => {
                return String(key)
            },
        });
    }

    private isValid(): boolean {
        switch (this.step()) {
            case Step.FormUpload:
                return !!this.file();
            case Step.ProcessUpload:
                return false;
            case Step.FormTransform: {
                const a = this.isMappingsValid();
                const b = !!this.isConfigurationValid();
                return a && b;
            }
            default:
                return true;
        }
    }

    private isMappingsValid(): boolean {
        return this.mappings()
            .map(m1 => {
                let a: boolean;
                switch (m1.mode()) {
                    case nsObjectUpload.AssignMode.Dynamic: {
                        a = !!m1.heading();
                        this.et.update(!a, m1.heading, 'invalid column');
                        break;
                    }
                    case nsObjectUpload.AssignMode.Static: {
                        a = !!m1.input();
                        this.et.update(!a, m1.input, 'invalid value');
                        break;
                    }
                }
                const b1 = !!m1.syncfield();
                const b2 = !this.mappings().find(m2 => m1 != m2 && m1.syncfield()?.name == m2.syncfield()?.name);
                if (!b1) {
                    this.et.update(true, m1.syncfield, 'invalid synchronization field');
                } else if (!b2) {
                    this.et.update(true, m1.syncfield, 'synchronization field already in use');
                } else {
                    this.et.update(false, m1.syncfield);
                }
                return a && b1 && b2;
            }).every(Boolean);
    }

    private isConfigurationValid(): boolean {
        global.sourceTranslation()
        if (!this.mappings().length) {
            this.mappingsMessageData({message: `no data`, type: 'warning'})
            return false;
        }

        const missingRequiredSyncfields = this.getMissingRequiredSyncfields();

        if (missingRequiredSyncfields.length) {
            this.mappingsMessageData({
                message: () => {
                    const labels = missingRequiredSyncfields.map(s => getSourceTranslation(s)).join(', ');
                    return lm.t(`missing mandatory synchronization field|n:${missingRequiredSyncfields.length}`) + ': ' + labels
                },
                type: 'warning'
            })
            return false;
        }

        this.mappingsMessageData(null);
        return true;
    }

    private getMissingRequiredSyncfields(): ISyncField[] {
        const syncfields = this.syncfields();
        if (!syncfields) {
            return [];
        }
        return syncfields
            .filter(sf => sf.required)
            .filter(sf => !this.mappings().find(m => m.syncfield()?.name == sf.name));
    }

    private onAddRequiredClick() {
        this.getMissingRequiredSyncfields().forEach(s => this.addMapping(s));
    }

    private onNextClick() {
        this.doNextStep();
    }

    private doNextStep() {
        this.step((this.step() ?? 0) + 1);
    }

    private onFileChange(vm: AbstractUpload, e: JQuery.ChangeEvent) {
        const files = (e.target as HTMLInputElement).files;
        const file = files?.[0] ?? null;
        this.file(file);
    }

    private onRestartClick() {
        this.step(Step.Init);
    }

    private isStep = isPartOf.bind(this, this.step);

    private parseCsv(csv: string | undefined): nsCsv.IParsedCsv {
        return nsCsv.parseCsv(csv ?? '', this.delimiter())
    }

    private onAddClick() {
        this.addMapping();
    }

    private addMapping(syncfield?: ISyncField) {
        const mapping: IMapping = {
            heading: ko.observable(''),
            syncfield: ko.observable(syncfield),
            mode: ko.observable<AssignMode>(AssignMode.Dynamic),
            input: ko.observable(null),
        }
        this.mappings.push(mapping);
    }

    private onDeleteClick(mapping: IMapping) {
        this.mappings.remove(mapping);
    }

    private getNumObjectsLabel(num: number) {
        return getNumObjectsLabel(num, this.__objectTypeId!, !!this.__routeDef.manual)
    }

    private getComboboxParams_heading(obj: IMapping): IComboboxV2Options<string> {
        return {
            initialMatch: o => o == obj.heading(),
            inputAttrs: {placeholder: lm.t('source')},
            data: this.parsedCsv.headings,
            option: {
                label: String,
            },
            selected: obj.heading as KnockoutObservable<string | null>,
            inputStatus: (selected) => selected ? 'ok' : 'warning',
        }
    }

    private getComboboxParams_syncfield(obj: IMapping): IComboboxV2Options<ISyncField> {
        return {
            inputAttrs: {placeholder: lm.t('target')},
            initialMatch: o => obj.syncfield()?.name == o.name,
            option: {
                label: o => getSourceTranslation(o),
                tooltip: o => o ? (lm.t(nsTooltip.getTitle_syncfield(o) ?? '') ?? '') : '',
            },
            // Factory — reloadWhen reloads must re-fetch syncfields (e.g. source translation).
            promise: () => nsSyncFields.getSyncFields(this.__objectTypeId!).request,
            selected: obj.syncfield as KnockoutObservable<ISyncField | null>,
            reloadWhen: global.sourceTranslation,
            inputStatus: (selected) => selected ? 'ok' : 'warning',
        }
    }

    override dispose() {
        this.kosub.unsuscribeAll();
        this.requests.forEach(r => r.abort());
    }
}